import { supabase } from "@/lib/supabase";

export type ProtocolKind = "move_in" | "move_out";

/**
 * The booking columns a protocol reopen writes (COL-427). Kind-scoped: each
 * kind only touches its own columns, and a fresh reopen supersedes a manual
 * close. Shared by the tenant profile and colivIQ Brain.
 */
export function reopenProtocolPatch(
  kind: ProtocolKind,
  now = new Date()
): Record<string, string | null> {
  const at = now.toISOString();
  return kind === "move_in"
    ? {
        move_in_auto_accepted_at: null,
        move_in_protocol_reopened_at: at,
        move_in_protocol_manually_closed_at: null,
      }
    : {
        move_out_auto_completed_at: null,
        move_out_protocol_reopened_at: at,
        move_out_protocol_manually_closed_at: null,
      };
}

/**
 * Reopen from a Brain card: the profile's write and audit action
 * (`<kind>_protocol_reopened`), tagged `source: "brain"`. The audit row is
 * shaped like the profile's (`metadata.booking_id` puts it on the Activity
 * timeline) and best-effort, as there.
 */
export async function reopenProtocolFromBrain(
  bookingId: number,
  kind: ProtocolKind,
  actorId: string | null
): Promise<void> {
  const now = new Date();
  const patch = reopenProtocolPatch(kind, now);
  const { data, error } = await supabase
    .from("bookings")
    .update({ ...patch, updated_at: now.toISOString() })
    .eq("id", bookingId)
    .select("id");
  if (error) throw error;
  // RLS or a deleted booking matches nothing — and supabase-js calls that success.
  if (!data?.length)
    throw new Error(
      "This booking could not be updated — nothing was reopened. Refresh and try again."
    );
  try {
    const { error: auditError } = await supabase.from("audit_logs").insert({
      user_id: actorId,
      action: `${kind}_protocol_reopened`,
      resource_type: "booking",
      resource_id: String(bookingId),
      old_values: null,
      new_values: patch,
      metadata: { booking_id: bookingId, source: "brain" },
    });
    // supabase-js reports a refused insert in `error`, it doesn't throw.
    if (auditError)
      console.warn("protocol reopen audit failed (ignored)", auditError);
  } catch (e) {
    console.warn("protocol reopen audit failed (ignored)", e);
  }
}
